import React, { FC, useState } from "react";
import {
    BatchBuildsData,
    BatchBuildsVariables,
    DropdownAction,
    Revision
} from "../../models/types";
import { Card, Col, Container, Row } from "react-bootstrap";
import { useMatch, useNavigate } from "react-router-dom";
import { useLazyQuery, useQuery } from "@apollo/client";
import {
    GET_BATCH_BUILDS,
    GET_PROJECT_EXPORT_FROM_REVISION
} from "../../graphql/queries";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSyncAlt } from "@fortawesome/free-solid-svg-icons";
import { checkBuildsStatus, getFormattedTime } from "../../common/Helpers";
import { ActionDropdown } from "../dropdowns/ActionDropdown";
// @ts-ignore
import "./simple.scss";

interface Props {
    revision?: Revision;
    projectId: number;
    showReleaseNotes: (id: number) => void;
    onPromote?: () => void;
    onRemove?: () => void;
}

const DevelopmentReleaseItemImplementation: FC<Props> = ({
    revision,
    projectId,
    showReleaseNotes,
    onPromote,
    onRemove
}) => {
    const navigate = useNavigate();
    const match = useMatch("/:path/*");
    const [exportText, updateExportText] = useState("Export");
    const { data } = useQuery<BatchBuildsData, BatchBuildsVariables>(
        GET_BATCH_BUILDS,
        {
            variables: {
                revisionId: revision?.id
            },
            skip: revision === undefined
        }
    );
    const [exportProject] = useLazyQuery<
        { getProjectExportFromRevision: string },
        { revisionId: number }
    >(GET_PROJECT_EXPORT_FROM_REVISION, {
        variables: { revisionId: Number(revision?.id) }
    });

    if (!revision) {
        return null;
    }

    const productId = revision?.project.product?.id;
    const variationsPath = `${match?.pathname}/${productId}/projects/${projectId}/revisions/${revision?.id}/variations`;
    let historyPath = `${match?.pathname}/${productId}/projects/${projectId}/revisions/${revision?.id}/history`;

    const { hasBuilds, hasRunning, latestCompleted } = checkBuildsStatus(data);

    if (latestCompleted) {
        historyPath += `/${latestCompleted.id}`;
    }

    const copyToClipboard = (exportData: string) => {
        navigator.clipboard.writeText(exportData).catch(error => {
            console.log("[DEBUG] copy to clipboard failed with error: ", error);
        });
    };

    const exportProjectFromRevision = async () => {
        const exportData = await exportProject();
        console.log("[DEBUG] exportData", exportData);
        if (exportData.data !== undefined) {
            copyToClipboard(exportData.data.getProjectExportFromRevision);
            updateExportText("Copied to clipboard");
            setTimeout(() => {
                updateExportText("Export");
            }, 5000);
        }
    };

    const actions: DropdownAction[] = [
        {
            header: "Edit",
            variant: "inverse",
            callback: () => navigate(variationsPath)
        },
        {
            header: hasRunning ? (
                <Container className="m-0 p-0">
                    <span className="text-start text-contrast">Builds</span>
                    {hasRunning ? (
                        <FontAwesomeIcon
                            icon={faSyncAlt}
                            size="1x"
                            className="text-contrast float-end ms-2 rotate-image-animation"
                        />
                    ) : null}
                </Container>
            ) : (
                "Build History"
            ),
            variant: hasBuilds ? "inverse" : "disabled",
            className: hasBuilds ? undefined : "disabled",
            callback: () => {
                if (hasBuilds) {
                    navigate(historyPath);
                }
            }
        },
        {
            header: "Changelog",
            variant: "inverse",
            callback: () => showReleaseNotes(revision?.id)
        },
        {
            header: exportText,
            variant: "purple",
            forceStayAfterClick: true,
            callback: () => {
                exportProjectFromRevision();
            }
        },
        {
            header: "Remove",
            variant: "danger",
            callback: () => {
                if (onRemove) {
                    onRemove();
                }
            }
        }
    ];

    return (
        <Card.Header
            onClick={e => {
                if (e.defaultPrevented) {
                    return;
                }
                navigate(variationsPath);
            }}
            className="text-contrast bread-text btn custom-card-body-children bg-info"
        >
            <Row className="d-flex align-items-center">
                <Col className="text-start ps-4 text-contrast revision-title col-7">
                    {revision?.name}
                </Col>
                <Col className="col-2 d-flex no-wrap text-contrast analytics-font justify-content-center">
                    {getFormattedTime(revision.dateUpdated)}
                </Col>
                <Col className="col-3 d-flex pe-1 justify-content-end">
                    <ActionDropdown
                        id={`dev-release-${revision.id}`}
                        variant="info"
                        actions={actions}
                    />
                </Col>
            </Row>
        </Card.Header>
    );
};

export const DevelopmentReleaseItem = React.memo(
    DevelopmentReleaseItemImplementation
);
